import React, { useEffect, useRef, useState } from 'react';
import {
  FaNodeJs,
  FaReact,
  FaAws,
  FaDocker,
  FaStripe,
  FaJava,
} from 'react-icons/fa';
import {
  SiPostgresql,
  SiMongodb,
  SiRedis,
  SiGraphql,
  SiFirebase,
  SiTailwindcss,
  SiVuedotjs,
  SiTensorflow,
  SiPytorch,
  SiShopify,
  SiPrisma,
  SiKotlin,
  SiMysql,
  SiElasticsearch,
  SiApachekafka,
  SiTerraform,
  SiJenkins,
  SiVercel,
  SiSupabase,
  SiOpenai,
  SiTypescript,
  SiNextdotjs,
  SiKubernetes,
} from 'react-icons/si';
import gsap from 'gsap';

const ROW_1 = [
  { name: 'Node.js', icon: <FaNodeJs /> },
  { name: 'PostgreSQL', icon: <SiPostgresql /> },
  { name: 'MongoDB', icon: <SiMongodb /> },
  { name: 'Docker', icon: <FaDocker /> },
  { name: 'AWS', icon: <FaAws /> },
  { name: 'OpenAI', icon: <SiOpenai /> },
  { name: 'TensorFlow', icon: <SiTensorflow /> },
  { name: 'Shopify', icon: <SiShopify /> },
  { name: 'React', icon: <FaReact /> },
  { name: 'TypeScript', icon: <SiTypescript /> },
];

const ROW_2 = [
  { name: 'Redis', icon: <SiRedis /> },
  { name: 'GraphQL', icon: <SiGraphql /> },
  { name: 'Firebase', icon: <SiFirebase /> },
  { name: 'Stripe', icon: <FaStripe /> },
  { name: 'Tailwind', icon: <SiTailwindcss /> },
  { name: 'Vue', icon: <SiVuedotjs /> },
  { name: 'PyTorch', icon: <SiPytorch /> },
  { name: 'Prisma', icon: <SiPrisma /> },
  { name: 'Next.js', icon: <SiNextdotjs /> },
  { name: 'Kubernetes', icon: <SiKubernetes /> },
];

const ROW_3 = [
  { name: 'Kotlin', icon: <SiKotlin /> },
  { name: 'Java', icon: <FaJava /> },
  { name: 'MySQL', icon: <SiMysql /> },
  { name: 'Elasticsearch', icon: <SiElasticsearch /> },
  { name: 'Kafka', icon: <SiApachekafka /> },
  { name: 'Terraform', icon: <SiTerraform /> },
  { name: 'Jenkins', icon: <SiJenkins /> },
  { name: 'Vercel', icon: <SiVercel /> },
  { name: 'Supabase', icon: <SiSupabase /> },
];

const TechStackSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    if (sectionRef.current) {
      const ctx = gsap.context(() => {
        gsap.to(".tech-grid-bg", {
          backgroundPosition: "100% 100%",
          duration: 40,
          repeat: -1,
          ease: "none",
        });
      }, sectionRef);

      return () => ctx.revert();
    }
  }, []);

  const MarqueeRow = ({ items, reverse = false, duration = '35s' }) => {
    const [paused, setPaused] = useState(false);
    const [hoveredIdx, setHoveredIdx] = useState(null);

    return (
      <div
        className="relative flex overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => { setPaused(false); setHoveredIdx(null); }}
      >
        <div
          className="flex gap-4 py-4"
          style={{
            animation: `marquee ${duration} linear infinite`,
            animationDirection: reverse ? 'reverse' : 'normal',
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {[...items, ...items, ...items].map((item, idx) => {
            const isHovered = hoveredIdx === idx;
            return (
              <div
                key={idx}
                className="relative flex-shrink-0"
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {/* Spotlight glow behind card — only on hover */}
                {isHovered && (
                  <div className="absolute inset-0 rounded-xl pointer-events-none"
                    style={{
                      background: 'radial-gradient(ellipse at 50% 50%, rgba(180,190,255,0.22) 0%, rgba(120,130,220,0.08) 55%, transparent 80%)',
                      filter: 'blur(6px)',
                      transform: 'scale(1.35)',
                    }}
                  />
                )}

                <div
                  className="relative flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 cursor-default select-none"
                  style={{
                    background: isHovered
                      ? 'linear-gradient(135deg, rgba(55,60,90,0.85) 0%, rgba(30,33,55,0.95) 100%)'
                      : 'rgba(17,20,35,0.5)',
                    backdropFilter: 'blur(8px)',
                    border: isHovered
                      ? '1px solid rgba(160,170,255,0.55)'
                      : '1px solid rgba(55,65,81,0.8)',
                    transform: isHovered ? 'translateY(-6px) scale(1.06)' : 'translateY(0) scale(1)',
                    boxShadow: isHovered
                      ? '0 0 12px rgba(140,150,255,0.35), 0 0 30px rgba(100,110,220,0.18), 0 8px 24px rgba(0,0,0,0.4)'
                      : 'none',
                    animation: isHovered ? 'techGlowPulse 1.6s ease-in-out infinite' : 'none',
                  }}
                >
                  {/* Icon */}
                  <span
                    className="text-2xl transition-all duration-300"
                    style={{
                      color: isHovered ? '#c8d0ff' : '#9ca3af',
                      transform: isHovered ? 'scale(1.25)' : 'scale(1)',
                      filter: isHovered ? 'drop-shadow(0 0 6px rgba(160,170,255,0.8))' : 'none',
                      display: 'block',
                    }}
                  >
                    {item.icon}
                  </span>

                  {/* Label */}
                  <span
                    className="text-sm font-medium transition-colors duration-300 whitespace-nowrap"
                    style={{
                      color: isHovered ? '#e8ecff' : '#9ca3af',
                      textShadow: isHovered ? '0 0 10px rgba(180,190,255,0.6)' : 'none',
                    }}
                  >
                    {item.name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-24 lg:py-32 overflow-hidden bg-transparent"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div 
          className="tech-grid-bg absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-gray-800 to-gray-950 rounded-full blur-3xl opacity-20" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-gray-900 to-black rounded-full blur-3xl opacity-20" />
      </div>

      {/* Main Content */}
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 lg:mb-20">
          {/* Section Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 rounded-full blur-md opacity-50" />
              <div className="relative flex items-center gap-2 px-4 py-2 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-full">
                <span className="text-xs font-medium tracking-wider text-gray-300 uppercase">
                  Tech Stack
                </span>
              </div>
            </div>
          </div>

          {/* Section Title */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
            <span className="text-white">Powered by modern</span>
            <br />
            <span className="bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 bg-clip-text text-transparent">
              technologies.
            </span>
          </h2>

          {/* Section Description */}
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            We master the tools that matter. From AI/ML frameworks to cloud
            infrastructure, our expertise spans the entire modern tech ecosystem.
          </p>
        </div>

        {/* Marquee Rows */}
        <div className="space-y-6 mt-16">
          <MarqueeRow items={ROW_1} duration="40s" />
          <MarqueeRow items={ROW_2} reverse duration="32s" />
          <MarqueeRow items={ROW_3} duration="38s" />
        </div>

        {/* Bottom Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 max-w-4xl mx-auto">
          {[
            { value: '30+', label: 'Technologies' },
            { value: '100%', label: 'Modern Stack' },
            { value: '24/7', label: 'Expert Support' },
            { value: '5+', label: 'Years Experience' },
          ].map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 bg-gray-900/30 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-gray-700 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="text-2xl md:text-3xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-xs tracking-wider text-gray-500 uppercase">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />

      {/* Marquee + glow keyframes */}
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes techGlowPulse {
          0%, 100% {
            box-shadow: 0 0 12px rgba(140,150,255,0.35), 0 0 30px rgba(100,110,220,0.18), 0 8px 24px rgba(0,0,0,0.4);
          }
          50% {
            box-shadow: 0 0 20px rgba(160,170,255,0.55), 0 0 50px rgba(120,130,240,0.28), 0 8px 24px rgba(0,0,0,0.4);
          }
        }
      `}</style>
    </section>
  );
};

export default TechStackSection;